/**
 * Every push of a video to machines and phones, newest first — and one of them in detail.
 *
 * The detail is assembled by the service from the job store and the in-flight pipeline, so what is shown
 * here is what actually happened: for a uniquified distribution first how many variants are rendered
 * ("Uniquification 50/100"), then per phone whether its variant is queued, rendering, done or given up
 * after three attempts; then the push — held until its release time, in flight with the machine's phase
 * and bytes (`JobPhase`), landed, or failed. Each phone has its own push job now, so the machine log is
 * per phone: "the push failed" is only useful together with the line that says why.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import type { Distribution, DistributionDevice, DistributionHub, Job, PublishProgress, UniquifyProgress } from '../../api/types'
import { useT } from '../../i18n'
import { useApp } from '../../lib/context'
import { usePoll } from '../../lib/poll'
import { toast } from '../../lib/toast'
import { Badge, Card, Empty, LogView, Pill, Status } from '../../ui/components'
import { ConfirmDanger } from '../../ui/ConfirmDanger'
import { Person } from '../../ui/Person'
import { ago, exactly } from '../../lib/when'
import { JobPhase } from './JobPhase'
import { useJobTail } from './useJobTail'

function kindOf(status: string): 'ok' | 'bad' | 'warn' | undefined {
  if (status === 'done') return 'ok'
  if (status === 'error') return 'bad'
  if (status === 'partial' || status === 'running' || status === 'queued' || status === 'pushing') return 'warn'
  return undefined
}

type Pending = { kind: 'stop' | 'delete'; d: Distribution; name: string }

/**
 * Stop a running distribution or remove a finished one: the two things a person can do to a distribution
 * after it started. Both are the service's doing (queue, live state, record); the UI only asks and confirms.
 */
function useDistributionActions(onDeleted?: (id: string) => void) {
  const { api, auth } = useApp()
  const t = useT()
  const [pending, setPending] = useState<Pending | null>(null)

  const run = async () => {
    if (!pending) return
    const { kind, d, name } = pending
    try {
      if (kind === 'stop') {
        await api.cancelDistribution(d.distribution_id)
        toast(t.distributions.stopped(name))
      } else {
        await api.deleteDistribution(d.distribution_id)
        toast(t.distributions.deleted(name))
        onDeleted?.(d.distribution_id)
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'bad')
    } finally {
      setPending(null)
    }
  }

  const release = async (d: Distribution, name: string) => {
    try {
      await api.releaseDistribution(d.distribution_id)
      toast(t.distributions.released(name))
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'bad')
    }
  }

  const buttons = (d: Distribution, name: string) =>
    d.status === 'running' && d.hold ? (
      <>
        <span className="tiny warn-text">{t.distributions.heldNote}</span>
        <button className="primary btn-sm" disabled={!auth.mayTouch(d.requested_by)}
                onClick={(e) => { e.stopPropagation(); void release(d, name) }}>
          {t.distributions.release}
        </button>
        <button className="ghost btn-sm" disabled={!auth.mayTouch(d.requested_by)}
                onClick={(e) => { e.stopPropagation(); setPending({ kind: 'stop', d, name }) }}>
          {t.distributions.stop}
        </button>
      </>
    ) : d.status === 'running' ? (
      <button className="ghost btn-sm" disabled={!auth.mayTouch(d.requested_by)}
              title={auth.mayTouch(d.requested_by) ? undefined : t.common.notYours}
              onClick={(e) => { e.stopPropagation(); setPending({ kind: 'stop', d, name }) }}>
        {t.distributions.stop}
      </button>
    ) : (
      <button className="ghost btn-sm" disabled={!auth.mayTouch(d.requested_by)}
              title={auth.mayTouch(d.requested_by) ? undefined : t.common.notYours}
              onClick={(e) => { e.stopPropagation(); setPending({ kind: 'delete', d, name }) }}>
        {t.distributions.delete}
      </button>
    )

  const dialog = pending && (
    <ConfirmDanger
      title={pending.kind === 'stop' ? t.distributions.stopTitle(pending.name) : t.distributions.deleteTitle(pending.name)}
      confirmLabel={pending.kind === 'stop' ? t.distributions.stop : t.distributions.delete}
      holdSeconds={pending.kind === 'stop' ? 2 : 1}
      onCancel={() => setPending(null)}
      onConfirm={() => void run()}
    >
      <p className="dim small">{pending.kind === 'stop' ? t.distributions.stopBody : t.distributions.deleteBody}</p>
    </ConfirmDanger>
  )

  return { buttons, dialog }
}

/** When a held push will be released, as the person sees it: "in 4 min" / "now". */
export function releaseIn(notBefore: number | null | undefined, nowMs: number): number | null {
  if (notBefore === null || notBefore === undefined) return null
  return Math.max(0, Math.round(notBefore - nowMs / 1000))
}

export function DistributionsPanel() {
  const { api, authEpoch } = useApp()
  const t = useT()
  const { id } = useParams()
  const navigate = useNavigate()
  const list = usePoll(() => api.distributions(), 3000, [api, authEpoch])
  const videos = usePoll(() => api.videos(), 15000, [api, authEpoch])
  const detail = usePoll(() => api.distribution(id!), 2000, [api, id], Boolean(id))
  const nameOf = (videoId: string) => videos.data?.find((v) => v.video_id === videoId)?.filename ?? videoId.slice(0, 8)
  const actions = useDistributionActions((deletedId) => {
    if (deletedId === id) navigate('/distributions')
    list.refresh()
  })

  return (
    <div className={id ? 'with-side-panel' : undefined}>
      {actions.dialog}
      {/* **The opened distribution is a sheet at the right edge** (redesign stage 3, from Device Lab's
          board): the list stays in view, so the eye keeps its place and the next row is one click away.
          The address is still `/distributions/<id>` — a link to one opens it the same way. */}
      {id && (
        <Sheet onClose={() => navigate('/distributions')}>
          {detail.data && (
            <Detail
              d={detail.data}
              name={nameOf(detail.data.video_id)}
              onBack={() => navigate('/distributions')}
              action={actions.buttons(detail.data, nameOf(detail.data.video_id))}
            />
          )}
          {detail.error && <Status text={t.distributions.readError(detail.error.message)} bad />}
        </Sheet>
      )}

      <Card title={t.distributions.title} sub={t.distributions.sub}>
        <Status text={list.error ? t.distributions.readError(list.error.message) : null} bad />
        {list.data && list.data.length === 0 && <Empty>{t.distributions.none}</Empty>}
        {list.data && list.data.length > 0 && (
          <div className="scroll-x">
            <table className="grid">
              <thead>
                <tr>
                  <th>{t.distributions.video}</th>
                  <th>{t.distributions.machines}</th>
                  <th>{t.distributions.uniquify}</th>
                  <th>{t.distributions.publishTitle}</th>
                  <th>{t.distributions.phones}</th>
                  <th>{t.distributions.state}</th>
                  <th>{t.distributions.started}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.data.map((d) => {
                  const phones = d.hubs.flatMap((h) => h.devices)
                  const done = phones.filter((p) => p.status === 'done').length
                  const failed = phones.filter((p) => p.status === 'error').length
                  return (
                    <tr
                      key={d.distribution_id}
                      className={d.distribution_id === id ? 'is-selected' : ''}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/distributions/${d.distribution_id}`)}
                    >
                      <td>
                        {nameOf(d.video_id)}
                        <div className="dim tiny mono">{d.distribution_id.slice(0, 8)}</div>
                      </td>
                      <td className="dim">{d.hubs.map((h) => h.hub_id).join(', ')}</td>
                      <td className="dim">{d.uniquify.enabled ? t.distributions.doneOf(d.uniquify.done, d.uniquify.total) : t.common.dash}</td>
                      <td className="dim">
                        {d.publish?.enabled ? t.distributions.doneOf(d.publish.done, d.publish.total) : t.common.dash}
                        {d.publish?.failed ? <span style={{ color: 'var(--bad)' }}> · {t.distributions.failed(d.publish.failed)}</span> : null}
                      </td>
                      <td className="dim">
                        {t.distributions.doneOf(done, phones.length)}
                        {failed > 0 && <span style={{ color: 'var(--bad)' }}> · {t.distributions.failed(failed)}</span>}
                      </td>
                      <td className="col-state">
                        <Badge kind={kindOf(d.status)}>{t.distributions.status[d.status]}</Badge>
                      </td>
                      <td className="dim small" title={exactly(d.created_at)}>
                        {d.created_at ? ago(d.created_at) : t.common.dash}
                        {d.requested_by && <div><Person who={d.requested_by} /></div>}
                      </td>
                      <td>{actions.buttons(d, nameOf(d.video_id))}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

/** A panel at the right edge **without a scrim** (owner, 2026-10-06: with it "все стало неклікабельне"
 *  and the page jumped). The list beside it stays live: another row opens in the same panel; Esc or the
 *  back button closes it. No scroll lock either — locking moved the page by the scrollbar's width. */
function Sheet({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return <aside className="dialog sheet side-panel" aria-label="details">{children}</aside>
}

function Detail({ d, name, onBack, action }: { d: Distribution; name: string; onBack: () => void; action: ReactNode }) {
  const t = useT()
  return (
    <Card
      title={name}
      sub={
        <span className="sheet-sub">
          {d.requested_by && <Person who={d.requested_by} />}
          {d.created_at && <span title={exactly(d.created_at)}>{ago(d.created_at)}</span>}
          {d.cancelled_at && (
            <span title={exactly(d.cancelled_at)}>
              {t.distributions.status.cancelled} {ago(d.cancelled_at)}
              {d.cancelled_by ? <> · <Person who={d.cancelled_by} /></> : null}
            </span>
          )}
        </span>
      }
      actions={
        <>
          <Badge kind={kindOf(d.status)}>{t.distributions.status[d.status]}</Badge>
          {action}
          <button className="ghost" onClick={onBack}>
            {t.distributions.back}
          </button>
        </>
      }
    >
      {d.uniquify.enabled && <UniquifyBar u={d.uniquify} />}
      {d.publish?.enabled && <PublishBar p={d.publish} />}
      {d.hubs.map((h) => (
        <HubRow key={h.hub_id} h={h} uniquified={d.uniquify.enabled} distributionId={d.distribution_id} />
      ))}
    </Card>
  )
}

/** "Uniquification 50/100" with a bar — the phase a person waits through before any phone is touched. */
function UniquifyBar({ u }: { u: UniquifyProgress }) {
  const t = useT()
  const settled = u.done + u.failed
  const percent = u.total > 0 ? Math.round((settled / u.total) * 100) : 0
  const kind = u.failed > 0 ? (u.done > 0 ? 'warn' : 'bad') : settled === u.total ? 'ok' : 'warn'
  return (
    <div className="row" style={{ padding: 'var(--sp-2) 0' }}>
      <Badge kind={kind}>
        {t.distributions.uniquify} {u.done}/{u.total}
      </Badge>
      <span className="job-phase-bar" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100} aria-label={t.distributions.uniquify}>
        <span className="job-phase-fill" style={{ width: `${percent}%` }} />
      </span>
      <span className="dim tiny">
        {u.rendering > 0 && <>{t.distributions.rendering(u.rendering)} · </>}
        {u.failed > 0 && <span style={{ color: 'var(--bad)' }}>{t.distributions.variantsFailed(u.failed)}</span>}
      </span>
    </div>
  )
}

/** "Posting 12/20" — the last phase, after the files landed; failures are settled, never retried by themselves. */
function PublishBar({ p }: { p: PublishProgress }) {
  const t = useT()
  const settled = p.done + p.failed
  const percent = p.total > 0 ? Math.round((settled / p.total) * 100) : 0
  const kind = p.failed > 0 ? (p.done > 0 ? 'warn' : 'bad') : settled === p.total ? 'ok' : 'warn'
  return (
    <div className="row" style={{ padding: 'var(--sp-2) 0' }}>
      <Badge kind={kind}>
        {t.distributions.publishTitle} {p.done}/{p.total}
      </Badge>
      <span className="job-phase-bar" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100} aria-label={t.distributions.publishTitle}>
        <span className="job-phase-fill" style={{ width: `${percent}%` }} />
      </span>
      <span className="dim tiny">
        {p.hand ? `${t.distributions.byClaude} · ` : p.scenario ? `${p.scenario} · ` : ''}
        {(p.by_hand ?? 0) > 0 && <>{t.distributions.publishState.by_hand} {p.by_hand} · </>}
        {(p.unconfirmed ?? 0) > 0 && <>{t.distributions.publishState.unconfirmed} {p.unconfirmed} · </>}
        {(p.not_found ?? 0) > 0 && <span style={{ color: 'var(--bad)' }}>{t.distributions.publishState.not_found} {p.not_found} · </span>}
        {p.running > 0 && <>{t.distributions.publishState.running} {p.running} · </>}
        {p.failed > 0 && <span style={{ color: 'var(--bad)' }}>{t.distributions.failed(p.failed)}</span>}
      </span>
    </div>
  )
}

function HubRow({ h, uniquified, distributionId }: { h: DistributionHub; uniquified: boolean; distributionId: string }) {
  const t = useT()
  return (
    <div style={{ borderTop: '1px solid var(--line-soft)', padding: 'var(--sp-3) 0' }}>
      <div className="row">
        <b>{h.hub_id}</b>
        <Badge kind={kindOf(h.status)}>{t.distributions.status[h.status]}</Badge>
      </div>
      <div className="sublist">
        {h.devices.map((x) => (
          <DeviceRow key={x.device_id} x={x} uniquified={uniquified} distributionId={distributionId} />
        ))}
      </div>
    </div>
  )
}

function DeviceRow({ x, uniquified, distributionId }: { x: DistributionDevice; uniquified: boolean; distributionId: string }) {
  const { api } = useApp()
  const t = useT()
  const [showLog, setShowLog] = useState(false)
  const [showPublishLog, setShowPublishLog] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const tail = useJobTail(showLog && x.job_id ? x.job_id : null)
  const publishTail = useJobTail(showPublishLog && x.publish?.job_id ? x.publish.job_id : null)
  const pub = x.publish && x.publish.status !== 'none' ? x.publish : null
  const pubWait = pub?.status === 'waiting' ? releaseIn(pub.not_before, Date.now()) : null

  const [pushing, setPushing] = useState(false)
  const pushTries = x.push_retries ?? 0
  const retryPush = async () => {
    setPushing(true)
    try {
      const r = await api.retryPush(distributionId, x.device_id)
      toast(t.distributions.pushRetried(x.device_id, r.try, r.of))
    } catch (e) {
      const detail = (e as { detail?: { detail?: { hint?: string } } })?.detail?.detail
      toast(detail?.hint ?? (e instanceof Error ? e.message : String(e)), 'bad')
    } finally {
      setPushing(false)
    }
  }

  const retry = async () => {
    setRetrying(true)
    try {
      await api.retryPublish(distributionId, x.device_id)
      toast(t.distributions.publishRetried(x.device_id))
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'bad')
    } finally {
      setRetrying(false)
    }
  }
  // `JobPhase` speaks in jobs; a phone's push entry is that job's live summary.
  const job: Job | null = x.job_id
    ? { job_id: x.job_id, status: x.status === 'pushing' ? 'running' : x.status === 'done' ? 'done' : x.status === 'error' ? 'error' : 'queued',
        phase: x.phase ?? null, progress: x.progress ?? null, error: x.error ?? null }
    : null
  const wait = x.status === 'pending' ? releaseIn(x.not_before, Date.now()) : null
  const variant = uniquified ? x.variant : null
  const waitingOnVariant = variant !== null && variant !== undefined && variant.status !== 'done' && variant.status !== 'none'

  return (
    <>
      <div className="subrow">
        <Pill mono>{x.device_id}</Pill>
        {variant && (
          <Badge kind={variant.status === 'done' ? 'ok' : variant.status === 'error' ? 'bad' : 'warn'} title={variant.error ?? undefined}>
            {t.distributions.variant[variant.status]}
            {variant.status !== 'done' && variant.attempts ? ` · ${t.distributions.attempt(variant.attempts)}` : ''}
          </Badge>
        )}
        {job && x.status === 'pushing' ? (
          <JobPhase job={job} />
        ) : (
          <Badge kind={kindOf(x.status)}>
            {x.status === 'pending' && !waitingOnVariant && wait !== null && wait > 0
              ? t.distributions.releaseIn(wait)
              : t.distributions.target[x.status]}
          </Badge>
        )}
        <span className="dim tiny mono" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {x.error ? <span style={{ color: 'var(--bad)' }}>{x.error}</span> : x.remote_path ?? ''}
        </span>
        <span className="grow" />
        {/* a failed push is delivered again on a click, at most 3 times — and the count is in sight, so
            nobody wonders whether it is retrying by itself (owner, 2026-10-15) */}
        {x.variant_url && (
          <a className="tiny" href={api.artifactUrl(x.variant_url)} target="_blank" rel="noreferrer">{t.distributions.viewVariant}</a>
        )}
        {pushTries > 0 && <span className="tiny dim">{t.distributions.pushTries(pushTries, 3)}</span>}
        {x.status === 'error' && variant?.status !== 'error' && (
          pushTries < 3
            ? <button className="btn-sm" disabled={pushing} onClick={() => void retryPush()}>{t.distributions.retryPush}</button>
            : <span className="tiny bad-text">{t.distributions.pushTriesUsed}</span>
        )}
        {x.job_id && (
          <button className="ghost btn-sm" aria-expanded={showLog} onClick={() => setShowLog((v) => !v)}>
            {showLog ? t.distributions.hideLog : t.distributions.showLog}
          </button>
        )}
      </div>
      {showLog && x.job_id && (
        <>
          <Status text={tail.error} bad />
          <LogView text={tail.log || t.distributions.emptyLog} height={160} />
        </>
      )}
      {pub && (
        <div className="subrow" style={{ paddingLeft: 'var(--sp-4)' }}>
          <Badge kind={pub.status === 'done' ? 'ok' : pub.status === 'error' || pub.status === 'not_found' ? 'bad' : pub.status === 'skipped' ? undefined : 'warn'}
                 title={pub.status === 'by_hand' ? t.distributions.byHandTitle
                   : pub.status === 'unconfirmed' ? t.distributions.unconfirmedTitle : undefined}>
            {pub.status === 'waiting' && pubWait !== null && pubWait > 0 ? t.distributions.publishIn(pubWait) : t.distributions.publishState[pub.status]}
          </Badge>
          {pub.url && (
            <a className="tiny" href={pub.url} target="_blank" rel="noreferrer">{t.distributions.openPost}</a>
          )}
          {(pub.status === 'error' || pub.status === 'not_found') && (
            <span className="dim tiny" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {pub.failed_step !== null && pub.failed_step !== undefined ? `${t.distributions.publishFailedAt(pub.failed_step)} · ` : ''}
              <span style={{ color: 'var(--bad)' }}>{pub.error}</span>
              {pub.screenshot && (
                <>
                  {' · '}
                  <a href={api.artifactUrl(pub.screenshot)} target="_blank" rel="noreferrer">
                    {t.distributions.publishScreenshot}
                  </a>
                </>
              )}
            </span>
          )}
          <span className="grow" />
          {(pub.status === 'error' || pub.status === 'skipped' || pub.status === 'not_found') && !pub.by_hand && (
            <button className="btn-sm" disabled={retrying} onClick={() => void retry()}>
              {t.distributions.publishRetry}
            </button>
          )}
          {pub.job_id && (
            <button className="ghost btn-sm" aria-expanded={showPublishLog} onClick={() => setShowPublishLog((v) => !v)}>
              {showPublishLog ? t.distributions.hideLog : t.distributions.publishLog}
            </button>
          )}
        </div>
      )}
      {showPublishLog && pub?.job_id && (
        <>
          <Status text={publishTail.error} bad />
          <LogView text={publishTail.log || t.distributions.emptyLog} height={160} />
        </>
      )}
    </>
  )
}
